import React from 'react';
import styled from 'styled-components';
import { Color, Card, Text } from '@bb/elements-web';
import {
  CanManageBillingProfileType,
  useSubscriptionsPageContext,
} from '../../SubscriptionsPageContext';
import ContactDetailsEmailCard from './ContactDetailsEmailCard';
import { CardStatus } from './CardStatus';
import ContactDetailsAddressCard from './ContactDetailsAddressCard';

export type ContactDetailsCardProps = {
  canManageBilling: CanManageBillingProfileType;
};

/**
 * The card displaying the user's Billing Customer number, email and address.
 * Also allows the user to edit these details from here.
 */
const ContactDetailsCard: React.FC<ContactDetailsCardProps> = ({
  canManageBilling,
}) => {
  const { billingProfile, hasSavedCard } = useSubscriptionsPageContext();
  const billingCustomerNumber = billingProfile?.UserBillingNumber;

  return (
    <Card
      title="Billing Contact Details"
      extra={
        billingCustomerNumber && (
          <Text color="PRIMARY_TEXT">Customer No. {billingCustomerNumber}</Text>
        )
      }
      dataCy="billing-contact-details-card"
    >
      <StyledBody>
        {canManageBilling.response === false && (
          <StyledCardStatus status="info" text={canManageBilling.message} />
        )}
        {!hasSavedCard && (
          <StyledCardStatus
            status="info"
            text="Credit card must be updated before you can update your email and address."
          />
        )}
      </StyledBody>
      {billingProfile ? (
        <StyledBody>
          <StyledSection>
            <ContactDetailsEmailCard canManageBilling={canManageBilling} />
          </StyledSection>

          <StyledSection>
            <ContactDetailsAddressCard canManageBilling={canManageBilling} />
          </StyledSection>
        </StyledBody>
      ) : (
        <Text color="SECONDARY_TEXT">No contact details found</Text>
      )}
    </Card>
  );
};

export default ContactDetailsCard;

const StyledBody = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  grid-row-gap: 25px;
  grid-column-gap: 25px;
  border-radius: 8px;
  align-items: start;
  flex-direction: column;
`;

const StyledSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border: 1px solid ${Color.LIGHTER_GREY};
  border-radius: 4px;
  padding: 16px;
`;

export const StyledContainer = styled.div`
  margin-bottom: 16px;
  border: 1px solid ${Color.LIGHTER_GREY};
  border-radius: 4px;
  display: flex;
  padding: 16px;
  width: 100%;
`;

const StyledCardStatus = styled(CardStatus)`
  margin-bottom: 16px;
`;
